/**
 * Lightbox — shared bottom-sheet: drag-to-dismiss, page-style scroll for tall content.
 *
 * Open:  click a [data-lightbox-open="<id>"] control → shows #<id>
 * Close: [data-lightbox-close] (backdrop or the × button),
 *        the Escape key, or dragging the sheet down by the top strip.
 *
 * Short list: the sheet fits the viewport. Long list: the whole overlay
 * scrolls (page-style) down to the end of the sheet; the header stays pinned.
 *
 * Markup: header.php · styles: src/css/components/lightbox.css
 */

const CLOSE_DRAG_DISTANCE = 110; // px the sheet must be pulled down to dismiss
const TRANSITION_FALLBACK = 400; // ms — safety net if transitionend never fires

let current = null;      // the open .lightbox, or null
let lastTrigger = null;  // element to restore focus to on close

function lockScroll( lock ) {
	document.body.style.overflow = lock ? 'hidden' : '';
}

/** Run cb once, when the sheet's transform transition ends (or after a fallback). */
function afterSheetTransition( sheet, cb ) {
	let done = false;

	const run = () => {
		if ( done ) {
			return;
		}
		done = true;
		sheet.removeEventListener( 'transitionend', onEnd );
		cb();
	};

	const onEnd = ( event ) => {
		if ( event.target === sheet && event.propertyName === 'transform' ) {
			run();
		}
	};

	sheet.addEventListener( 'transitionend', onEnd );
	setTimeout( run, TRANSITION_FALLBACK );
}

export function openLightbox( lightbox, trigger ) {
	if ( current && current !== lightbox ) {
		closeLightbox( current, false );
	}

	current = lightbox;
	lastTrigger = trigger || null;

	lightbox.hidden = false;
	lightbox.scrollTop = 0;
	void lightbox.offsetWidth; // reflow so the transition runs from the hidden state
	lightbox.classList.add( 'is-open' );
	lockScroll( true );

	const sheet = lightbox.querySelector( '.lightbox__sheet' );
	if ( sheet ) {
		afterSheetTransition( sheet, () => {
			if ( current === lightbox ) {
				lightbox.classList.add( 'is-scrollable' );
			}
		} );
	}

	const close = lightbox.querySelector( '.lightbox__close' );
	if ( close ) {
		close.focus( { preventScroll: true } );
	}
}

export function closeLightbox( lightbox, restoreFocus = true ) {
	const sheet = lightbox.querySelector( '.lightbox__sheet' );

	lightbox.classList.remove( 'is-scrollable', 'is-dragging' );
	lightbox.scrollTop = 0;
	if ( sheet ) {
		sheet.style.transform = '';
	}
	lightbox.classList.remove( 'is-open' );

	if ( sheet ) {
		afterSheetTransition( sheet, () => {
			lightbox.hidden = true;
		} );
	} else {
		lightbox.hidden = true;
	}

	if ( current === lightbox ) {
		current = null;
		lockScroll( false );
	}

	if ( restoreFocus && lastTrigger ) {
		lastTrigger.focus();
	}
	lastTrigger = null;
}

function initDrag( lightbox ) {
	const handle = lightbox.querySelector( '.lightbox__handle' );
	const sheet = lightbox.querySelector( '.lightbox__sheet' );
	if ( ! handle || ! sheet ) {
		return;
	}

	let startY = 0;
	let deltaY = 0;
	let dragging = false;

	const onMove = ( event ) => {
		if ( ! dragging ) {
			return;
		}
		deltaY = Math.max( 0, event.clientY - startY );
		sheet.style.transform = `translateY(${ deltaY }px)`;
	};

	const onUp = () => {
		if ( ! dragging ) {
			return;
		}
		dragging = false;
		lightbox.classList.remove( 'is-dragging' );
		window.removeEventListener( 'pointermove', onMove );
		window.removeEventListener( 'pointerup', onUp );
		window.removeEventListener( 'pointercancel', onUp );

		if ( deltaY > CLOSE_DRAG_DISTANCE ) {
			closeLightbox( lightbox );
		} else {
			sheet.style.transform = ''; // snap back — transition is on again
		}
	};

	handle.addEventListener( 'pointerdown', ( event ) => {
		dragging = true;
		startY = event.clientY;
		deltaY = 0;
		lightbox.classList.add( 'is-dragging' );
		window.addEventListener( 'pointermove', onMove );
		window.addEventListener( 'pointerup', onUp );
		window.addEventListener( 'pointercancel', onUp );
	} );
}

function initLightboxes() {
	const lightboxes = document.querySelectorAll( '[data-lightbox]' );
	if ( ! lightboxes.length ) {
		return;
	}

	lightboxes.forEach( initDrag );

	document.addEventListener( 'click', ( event ) => {
		const trigger = event.target.closest( '[data-lightbox-open]' );
		if ( trigger ) {
			const lightbox = document.getElementById( trigger.getAttribute( 'data-lightbox-open' ) );
			if ( lightbox ) {
				event.preventDefault();
				openLightbox( lightbox, trigger );
			}
			return;
		}

		if ( current && event.target.closest( '[data-lightbox-close]' ) ) {
			closeLightbox( current );
		}
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( ! current ) {
			return;
		}

		if ( event.key === 'Escape' ) {
			closeLightbox( current );
			return;
		}

		if ( event.key === 'Tab' ) {
			const focusable = current.querySelectorAll(
				'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
			);
			if ( ! focusable.length ) {
				return;
			}
			const first = focusable[ 0 ];
			const last = focusable[ focusable.length - 1 ];

			if ( event.shiftKey && document.activeElement === first ) {
				event.preventDefault();
				last.focus();
			} else if ( ! event.shiftKey && document.activeElement === last ) {
				event.preventDefault();
				first.focus();
			}
		}
	} );
}

initLightboxes();
